import React, {
	useEffect,
	useMemo,
	useRef,
	useCallback
} from "react";
import { useSelector } from "react-redux";

import SectionHeader from "@cayuse/shared-react.components.section-header";
import { isNonEmptyArray } from "@cayuse/shared-react.utils.type-checks";
import produce from "immer";


import ReportingLoader from "containers/Reporting/Loader";
import {
	FORM_SUBMISSION_TYPES,
	PROPOSAL_REPORT_LIST_DEFAULT_COLUMNS,
	AWARDS_REPORT_LIST_DEFAULT_COLUMNS,
	SUBAWARDS_REPORT_LIST_DEFAULT_COLUMNS,
	REPORT_DATASOURCE_TYPE,
	REPORT_GRID_TYPE,
	AWARDS,
	PROPOSALS,
	SUBAWARDS_ALT,
} from "globals/constants";
import {
	useDictionary,
	useSessionStorage,
	useBinaryToggle,
	useDownloadCsv } from "hooks";
import {
	reportsTypeCheck,
	generateDatedDownloadFileName,
	createJSONBlobURL,
	notifyError,
	getDateTimeLocale,
} from "utils";

import ReportingGrid from "./ReportingGrid";
import styles from "./styles.css";

const REPORT_LIST_DEFAULT_COLUMNS = {
	[AWARDS]: AWARDS_REPORT_LIST_DEFAULT_COLUMNS,
	[SUBAWARDS_ALT]: SUBAWARDS_REPORT_LIST_DEFAULT_COLUMNS,
	[PROPOSALS]: PROPOSAL_REPORT_LIST_DEFAULT_COLUMNS
};
const REPORT_FORM_SUBMISSION_TYPES = {
	[AWARDS]: FORM_SUBMISSION_TYPES.AWARD,
	[SUBAWARDS_ALT]: FORM_SUBMISSION_TYPES.SUBAWARD,
	[PROPOSALS]: FORM_SUBMISSION_TYPES.PROPOSAL
};

const getPageHeader = (route, dictionary) => {
	return reportsTypeCheck(
		route,
		dictionary.awardHeader ?? "Award Reporting",
		dictionary.subawardHeader ?? "Subaward Reporting",
		dictionary.proposalHeader ?? "Proposal Reporting");
};

const getDownloadCsvFilename = route => {
	return generateDatedDownloadFileName({
		prefix: reportsTypeCheck(route, FORM_SUBMISSION_TYPES.AWARD, FORM_SUBMISSION_TYPES.SUBAWARD, FORM_SUBMISSION_TYPES.PROPOSAL),
		extension: "json",
	});
};

const dateTimeFormat = getDateTimeLocale();

const initialReportState = {
	localization: { grid: { blankMember: "",  dateInvalidCaption: "" } },
	formats: [
		{
			name: "",
			thousandsSeparator: " ",
			decimalSeparator: ".",
			decimalPlace: 2,
			maxSymbols: 20,
			currencySymbol: "",
			currencySymbolAlign: "left",
			infinityValue: "Infinity",
			divideByZeroValue: "Infinity",
		},
	],
	options: {
		grid: {
			type: REPORT_GRID_TYPE,
			showTotals: "off",
			showGrandTotals: "off",
		},
		showAggregations: false,
		configuratorActive: false,
		showCalculatedValuesButton: false,
		datePattern: dateTimeFormat.date,
		dateTimePattern: `${dateTimeFormat.date} ${dateTimeFormat.reportTime}`,
	}
};

const standardize = t => t?.toLowerCase().trim() || null;
const uniques = (item, k, arr) => item && arr.indexOf(item) === k;
const concat = (...arrays) => [].concat(...arrays.filter(Array.isArray)).filter(uniques);
const ReportingGridView = props => {
	const { match } = props;
	const route = match.path.split("/").filter(Boolean).pop();

	// Setting a key here avoids issues related to navigating between
	// different reports. The key will cause the component to unmount
	// and be recreated instead of leaking state between report types.
	return <FlatReportView key={route} route={route} />;
};

const FlatReportView = props => {
	const { route } = props;
	const reportRef = useRef();
	const uploadedSliceRef = useRef(null);

	const tenantFormColumns = useSelector(state => state?.tenant?.formColumnHeaders);
	const formColumns = reportsTypeCheck(route, tenantFormColumns?.AWARD, tenantFormColumns?.SUBAWARD, tenantFormColumns?.PROPOSAL);
	const defaultColumns = REPORT_LIST_DEFAULT_COLUMNS[route];
	const defaultReport = useMemo(() => ({
		...initialReportState,
		slice: {
			columns: defaultColumns.map(col => ({ uniqueName: col.columnHeader }))
		}
	}), [ defaultColumns ]);
	const allColumns = useMemo(() => {
		return Object.values([ formColumns, defaultColumns ]
			.flat()
			.reduce((a, v) => {
				const header = standardize(v?.columnHeader);
				if(header && v.keys)
				{
					a[header] = produce(v, draft => {
						draft.keys = concat(v.keys, a[header]?.keys);
					});
				}

				return a;
			}, {}));
	}, [ formColumns, defaultColumns ]);
	const [ ready, setReady ] = useBinaryToggle(false);

	const [ report, storeReport ] = useSessionStorage(`${route}:reportConfig`, defaultReport);

	const setReport = useCallback(next => {
		return storeReport(state => {
			// Allow same usage as useState
			const newReport = typeof next === "function" ? next(state) : next;

			// Update the Flexmonster instance
			// eslint-disable-next-line no-unused-expressions
			reportRef.current?.flexmonster.setReport(newReport);

			return newReport;
		});
	}, [ storeReport ]);

	const onReportChange = useCallback(() => {
		// Update the report config in session storage
		storeReport(reportRef.current?.flexmonster.getReport());
	}, [ reportRef, storeReport ]);

	const [ loading, refetch, loaded ] = useBinaryToggle(true);

	const reportFieldsRef = useRef([]);
	const reportFields = useMemo(() => {
		const { slice } = report;

		// List of column headers currently in the report
		// We use this to check if we need to fetch new data from the API
		const reportColNames = [ slice?.columns, slice?.rows ]
			.flatMap(a => a ?? [])
			.map(col => col?.uniqueName)
			.map(standardize)
			.filter(uniques);

		// List of keys extracted from tenant form columns if uniqueName & columnHeader match
		const reportFields = allColumns
			.filter(col => col.keys && col.columnHeader)
			.filter(col => reportColNames.includes(standardize(col?.columnHeader)))
			.flatMap(col => col.keys) // concat all keys into one array
			.filter(uniques);

		return reportFields;
	}, [ report, allColumns ]);

	// Check if we need new data from the API
	useEffect(() => {
		const prev = reportFieldsRef.current;

		// If any of the new fields weren't already in previous report fields,
		// then we need to refresh the data
		const refreshData = reportFields.some(key => !prev.includes(key));
		if (refreshData) {
			reportFieldsRef.current = reportFields;
			refetch();
		}
	}, [ reportFields, refetch ]);

	const { download } = useDownloadCsv();
	useEffect(() => {
		const fileData = getDownloadCsvFilename(route);
		const reportFields = reportFieldsRef.current;
		let cancelReq = false;
		const reloadReportData = async () => {
			const payload = {
				submissionType: REPORT_FORM_SUBMISSION_TYPES[route],
				fields: reportFields.join(","),
				filename: fileData,
			};

			const blob = await download(payload, true);
			if (blob) {
				const text = await blob.text();

				const [ objectUrl, fieldMapping ] = createJSONBlobURL(
					text,
					allColumns
				);
				// avoid setting state if component was unmounted while the request was in flight
				if (!cancelReq) {
					// Update the report config. flexmonster.updateData has a bug
					// where it doesn't properly conserve the localization configs
					// so we will have to manually copy over the report settings ourseslves
					// until it's fixed.
					setReport(
						produce(report => {
							report.dataSource = {
								filename: objectUrl,
								dataSourceType: REPORT_DATASOURCE_TYPE,
								mapping: fieldMapping
							};
						})
					);

					// Apply uploaded slice if present to reset ignored filters after data reload
					if (uploadedSliceRef.current) {
						setReport(
							produce(report => {
								report.slice = uploadedSliceRef.current;
							})
						);
						uploadedSliceRef.current = null;
					}

					loaded();
				}
			}
		};

		if (ready && loading && isNonEmptyArray(reportFields)) {
			reloadReportData();
		}

		return () => cancelReq = true;

	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ loading, ready, allColumns, reportFieldsRef ]);

	const resetReport = useCallback(() => {
		setReport(report => {
			return produce(defaultReport, draft => {
				draft.dataSource = report?.dataSource;
			});
		});
	}, [ setReport, defaultReport ]);

	const dictionary = useDictionary("reporting");
	const pageHeader = getPageHeader(route, dictionary);

	const onUploadError = useCallback(ex => {
		const message = dictionary.errors?.errorParsingJson
			|| ex?.error
			|| "Saved Report has incorrect format.";

		notifyError(message, ex);
	}, [ dictionary ]);

	return (
		<div id="view-main-reporting" className={styles.viewMainReporting}>
			<SectionHeader header={pageHeader} />
			{loading && <ReportingLoader />}
			<ReportingGrid
				reportRef={reportRef}
				reportchange={onReportChange}
				restore={resetReport}
				ready={setReady}
				hidden={!ready}
				dataerroor={onUploadError}
				onReportUpload={slice => {
					uploadedSliceRef.current = slice;
				}}
			/>
		</div>
	);
};

export default ReportingGridView;
